// ink-component
import React, { useState, useRef, ReactNode } from 'react';
import { ThemeContext, ThemeProvider } from 'styled-components';
import { usePopper } from 'react-popper';
import type { StrictModifiers } from '@popperjs/core';

import useClickOutside from '../../hooks/useClickOutside';
import getGlobalProps from '../../helpers/constants/global-props';
import Portal from '../_common/Portal';
import { TooltipWrapper, TooltipTrigger, TooltipContent, TooltipArrow } from './styles';
import { ErrorMark } from '../_common/Controller/components/ControllerError/ControllerError';

export interface TooltipProps {
  /** Content node */
  children: ReactNode;
  /** Trigger node. Ensure tooltip functionality is accessible by using a [focusable trigger](https://stackoverflow.com/a/1600194). */
  trigger: ReactNode;
  /** The direction the tooltip will be opened */
  placement?: 'auto' | 'right' | 'left' | 'top' | 'bottom';
  /** Trigger is block element */
  triggerIsBlock?: boolean;
  /** Prevent tooltip content to persist open when trigger is clicked. **Attention**:
   * if you turn this off, mobile users won‘t benefit from the click-to-open built in behavior. */
  preventClickPersist?: boolean;
  /** Removes time delay between trigger losing hover/focus and the dismissal of the tooltip content */
  noDelay?: boolean;
  /** @ignore */
  isShowing?: boolean;
  /** The `feedback-negative` variant is designed for showing errors within tables. Read [Handling errors in specific cells](/components/NewTable/usage#handling-errors-in-specific-cells) for details.  */
  variant?: 'feedback-negative';
  /** Useful for tracking engagement */
  onHover?: () => void;
  /** Customize the Tooltip behavior passing an array to configure
   * any of the **prebuilt** Popper v2 modifiers:
   * https://popper.js.org/docs/v2/constructors/#modifiers */
  modifiers?: StrictModifiers[] | [];
}

const globalProps = getGlobalProps({});

/**
 * @purpose Show complementary information on demand
 * @categories accessory
 * @aka help
 */
export function Tooltip(props: TooltipProps) {
  const {
    children,
    modifiers = [],
    trigger,
    placement,
    triggerIsBlock,
    preventClickPersist,
    noDelay,
    variant,
    isShowing = true,
    onHover,
  } = props;
  const [open, setOpen] = useState(false);
  const [closeTimer, setCloseTimer] = useState(0);

  const themeContext = React.useContext(ThemeContext);

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const popperRef = useRef<HTMLDivElement>(null);

  const onClickOutside = e => {
    if (!(popperRef && popperRef.current && popperRef.current.contains(e.target))) setOpen(false);
  };
  const [focus, setFocus, ref] = useClickOutside(onClickOutside);

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      ...modifiers,
      {
        name: 'arrow',
        options: { element: arrowElement },
      },
    ],
  });

  const openTooltip = () => {
    clearTimeout(closeTimer);
    if (!open && isShowing) {
      if (update) update();
      setOpen(true);
    }
  };

  const handleClick = () => {
    setFocus(!preventClickPersist && true);
    openTooltip();
  };

  const handleMouseEnter = () => {
    openTooltip();
  };

  const handleFocus = () => {
    openTooltip();
  };

  const handleMouseLeave = () => {
    if (!focus) {
      const timeoutId = window.setTimeout(
        () => {
          setOpen(false);
        },
        noDelay ? 0 : 250,
      );
      setCloseTimer(timeoutId);
    }
  };

  const handleBlur = () => {
    setFocus(false);
    handleMouseLeave();
  };

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        tooltipBlock: triggerIsBlock,
        tooltipVariant: variant,
      })}
    >
      <TooltipWrapper ref={ref} data-testid={props['data-testid']} {...globalProps}>
        <TooltipTrigger
          aria-describedby="tooltip-content"
          data-testid="tooltip-trigger"
          ref={setReferenceElement}
          onClick={handleClick}
          onMouseEnter={() => {
            handleMouseEnter();
            onHover && onHover();
          }}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {trigger}
        </TooltipTrigger>
        {open && (
          <Portal>
            <div ref={popperRef}>
              <TooltipContent
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                id="tooltip-content"
                role="tooltip"
                ref={setPopperElement}
                style={styles.popper}
                {...attributes.popper}
              >
                {variant === 'feedback-negative' && (themeContext ? themeContext.isTable : false) && <ErrorMark />}
                {children}
                <TooltipArrow ref={setArrowElement} style={styles.arrow} />
              </TooltipContent>
            </div>
          </Portal>
        )}
      </TooltipWrapper>
    </ThemeProvider>
  );
}

Tooltip.defaultProps = {
  placement: 'auto',
  triggerIsBlock: false,
  preventClickPersist: false,
  noDelay: false,
  modifiers: [],
  isShowing: true,
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
